import { useEffect, useMemo, useState } from "react";
import { Users, Save } from "lucide-react";
import { toast } from "sonner";
import LevelGrid, { LEVEL_OPTS, LEVEL_HINT } from "./LevelGrid";
import { ACCESS_ROLE_LABELS, PRESET_ROLE_ORDER, fetchPresets, savePreset } from "../../lib/access";

export function LevelLegend() {
  return (
    <div className="flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-slate-400 mb-3" data-testid="level-legend">
      {LEVEL_OPTS.map(([k, l]) => <span key={k}><b className="text-slate-200">{l}</b> · {LEVEL_HINT[k]}</span>)}
    </div>
  );
}

export default function PresetManager({ groups }) {
  const [presets, setPresets] = useState([]);
  const [activeRole, setActiveRole] = useState("operator");
  const [lv, setLv] = useState({});
  const [caps, setCaps] = useState(new Set());
  const [saving, setSaving] = useState(false);
  const active = useMemo(() => presets.find((p) => p.role === activeRole), [presets, activeRole]);
  const isAdmin = activeRole === "admin";

  const load = async () => setPresets(await fetchPresets());
  useEffect(() => { load(); }, []);
  useEffect(() => {
    if (active) { setLv({ ...(active.levels || {}) }); setCaps(new Set(active.capabilities || [])); }
  }, [active]);

  const save = async () => {
    setSaving(true);
    try {
      const res = await savePreset(activeRole, lv, [...caps]);
      toast.success(`${ACCESS_ROLE_LABELS[activeRole]} preset saved · applies to ${res.applies_to.total} ${res.applies_to.total === 1 ? "person" : "people"}`);
      await load();
    } catch { toast.error("Failed to save preset"); }
    finally { setSaving(false); }
  };

  return (
    <div>
      <div className="flex flex-wrap gap-2 mb-4" data-testid="preset-role-tabs">
        {PRESET_ROLE_ORDER.map((r) => {
          const p = presets.find((x) => x.role === r);
          return (
            <button key={r} onClick={() => setActiveRole(r)} data-testid={`preset-tab-${r}`}
              className={`px-3.5 py-1.5 rounded-full text-sm font-medium border transition-colors ${activeRole === r ? "bg-orange-500/15 border-orange-400/50 text-orange-200" : "border-white/10 text-slate-300 hover:bg-white/5"}`}>
              {ACCESS_ROLE_LABELS[r]}
              {p && <span className="ml-1.5 text-[10px] font-mono text-slate-400">{p.applies_to.total}</span>}
            </button>
          );
        })}
      </div>
      {active && (
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2 text-xs text-slate-400" data-testid="preset-applies">
            <Users className="w-3.5 h-3.5" />
            Applies to <b className="text-slate-200">{active.applies_to.total - active.applies_to.customized}</b> of <b className="text-slate-200">{active.applies_to.total}</b>
            {active.applies_to.customized > 0 && <span>({active.applies_to.customized} with custom access)</span>}
          </div>
          {!isAdmin && <button onClick={save} disabled={saving} className="btn-coral inline-flex items-center gap-1.5 disabled:opacity-40" data-testid="preset-save">
            <Save className="w-3.5 h-3.5" /> {saving ? "Saving…" : "Save preset"}
          </button>}
        </div>
      )}
      <p className="text-[11px] text-slate-500 mb-3">
        {isAdmin ? "Admins always have Manage on every area, plus Config and user management. Pick another role to edit it."
          : <>Changes here apply to every <b>{ACCESS_ROLE_LABELS[activeRole]}</b>. Anything set individually on a person stays as it is.</>}
        {activeRole === "supplier" && " Supplier stays empty until the supplier area is built."}
      </p>
      <LevelLegend />
      <LevelGrid groups={groups} value={lv} base={null} onChange={setLv} locked={isAdmin}
        abilities={isAdmin ? null : { mode: "preset", value: caps, onChange: setCaps, testid: "preset-abilities" }} />
    </div>
  );
}
